import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal, flushSync } from 'react-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import { FiEdit2, FiPlus, FiSave, FiTrash2, FiX } from 'react-icons/fi';
import './AdminPages.css';

const DBG = '[KYC DocTypes]';

/** Above App Loader (~99999) and toast container */
const MODAL_Z = 2_147_483_000;
const PORTAL_ROOT_ID = 'kyc-doc-types-portal-root';
const OVERLAY_MARK = 'data-kyc-modal-overlay';

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const rowId = (row) => Number(row?.id ?? row?._id);

const KycDocumentTypes = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', is_active: true });

  /** Keep portal host in state (not ref+flag) — after Strict Mode remount, refs can be stale during render while `panelOpen` turns true; state stays in sync. */
  const [portalEl, setPortalEl] = useState(() =>
    typeof document !== 'undefined' ? document.getElementById(PORTAL_ROOT_ID) : null
  );

  const addBtnRef = useRef(null);
  const panelOpenRef = useRef(panelOpen);
  panelOpenRef.current = panelOpen;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/kyc/admin/document-types`, { headers: authHeaders() });
      const raw = res.data;
      let list = [];
      if (Array.isArray(raw)) list = raw;
      else if (Array.isArray(raw?.data)) list = raw.data;
      setRows(list);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    console.log(DBG, 'mount route');
    return () => console.log(DBG, 'unmount route');
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Ensure #kyc-doc-types-portal-root exists and store in state */
  useLayoutEffect(() => {
    try {
      let el = document.getElementById(PORTAL_ROOT_ID);
      if (!el) {
        el = document.createElement('div');
        el.id = PORTAL_ROOT_ID;
        document.body.appendChild(el);
        console.log(DBG, 'portal host created');
      } else {
        console.log(DBG, 'portal host reused, children=', el.childElementCount);
      }
      setPortalEl(el);
    } catch (err) {
      console.error(DBG, 'portal host FAILED', err);
      toast.error('Could not attach modal to page — check console');
    }
  }, []);

  const closePanel = useCallback(() => {
    console.log(DBG, 'closePanel()');
    setPanelOpen(false);
    setEditRow(null);
    setForm({ name: '', description: '', is_active: true });
  }, []);

  const openPanelForCreate = useCallback(() => {
    if (!portalEl) {
      console.error(DBG, 'open blocked: portalEl is null — layout not ready yet');
      toast.error('Still preparing the page — click again.');
      return;
    }
    console.log(DBG, 'openPanelForCreate()', { portalId: portalEl.id });
    try {
      flushSync(() => {
        setEditRow(null);
        setForm({ name: '', description: '', is_active: true });
        setPanelOpen(true);
      });
      console.log(DBG, 'after flushSync, panelOpenRef=', panelOpenRef.current);
    } catch (err) {
      console.error(DBG, 'flushSync open FAILED', err);
      toast.error('Could not open form');
    }
  }, [portalEl]);

  /** DOM listener: works even if React synthetic onClick fails (delegation quirks, overlays during capture). */
  useEffect(() => {
    const el = addBtnRef.current;
    if (!el) return;
    const onDomClick = (e) => {
      console.log(DBG, 'Add button DOM click (isTrusted=', e.isTrusted, ')');
      e.stopPropagation();
      openPanelForCreate();
    };
    el.addEventListener('click', onDomClick, false);
    return () => el.removeEventListener('click', onDomClick, false);
  }, [openPanelForCreate]);

  useEffect(() => {
    if (!panelOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [panelOpen]);

  useEffect(() => {
    console.log(DBG, 'panelOpen →', panelOpen, 'portalEl →', portalEl?.id ?? 'null');
  }, [panelOpen, portalEl]);

  useLayoutEffect(() => {
    if (!panelOpen) return;
    requestAnimationFrame(() => {
      const node = document.querySelector(`[${OVERLAY_MARK}="1"]`);
      console.log(DBG, 'post-open overlay in DOM?', !!node, node?.getBoundingClientRect());
    });
  }, [panelOpen]);

  useEffect(() => {
    if (!panelOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [panelOpen, closePanel]);

  useEffect(() => {
    window.__KYC_DOC_TYPES_MODAL_DEBUG = {
      portalEl,
      portalId: PORTAL_ROOT_ID,
      forceOpenCreate: () => {
        flushSync(() => {
          setEditRow(null);
          setForm({ name: '', description: '', is_active: true });
          setPanelOpen(true);
        });
      },
      forceClose: closePanel,
    };
    return () => {
      delete window.__KYC_DOC_TYPES_MODAL_DEBUG;
    };
  }, [closePanel, portalEl]);

  const openPanelForEdit = (row) => {
    if (!portalEl) {
      toast.error('Still preparing — try again.');
      return;
    }
    const id = rowId(row);
    console.log(DBG, 'open edit id=', id);
    try {
      flushSync(() => {
        setEditRow(id ? { ...row, id } : row);
        setForm({
          name: row.name || '',
          description: row.description || '',
          is_active: row.is_active !== false,
        });
        setPanelOpen(true);
      });
    } catch (err) {
      console.error(DBG, 'edit open FAILED', err);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const wasEdit = Boolean(editRow);
    try {
      if (editRow) {
        await axios.put(`${API_URL}/kyc/admin/document-types/${editRow.id}`, form, { headers: authHeaders() });
      } else {
        await axios.post(`${API_URL}/kyc/admin/document-types`, form, { headers: authHeaders() });
      }
      toast.success(`Document type ${wasEdit ? 'updated' : 'created'}`);
      closePanel();
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Save failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this document type?')) return;
    try {
      await axios.delete(`${API_URL}/kyc/admin/document-types/${id}`, { headers: authHeaders() });
      toast.success('Deleted');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Delete failed');
    }
  };

  const modalLayer =
    panelOpen &&
    portalEl &&
    createPortal(
      <div
        {...{ [OVERLAY_MARK]: '1' }}
        role="presentation"
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          minHeight: '100vh',
          margin: 0,
          padding: '1rem',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: MODAL_Z,
          backgroundColor: 'rgba(15, 23, 42, 0.72)',
          overflow: 'auto',
          pointerEvents: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) closePanel();
        }}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="kyc-doc-type-modal-title"
          className="admin-modal"
          style={{
            maxWidth: 700,
            width: '100%',
            maxHeight: 'min(90vh, 900px)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h2 id="kyc-doc-type-modal-title">{editRow ? 'Edit' : 'Add'} Document Type</h2>
            <button type="button" className="btn-icon" onClick={closePanel} aria-label="Close"><FiX /></button>
          </div>
          <form className="modal-form" style={{ overflowY: 'auto', flex: 1 }} onSubmit={save}>
            <div className="form-group">
              <label htmlFor="kyc-doc-type-name">Name *</label>
              <input
                id="kyc-doc-type-name"
                autoComplete="off"
                required
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label htmlFor="kyc-doc-type-description">Description</label>
              <textarea
                id="kyc-doc-type-description"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="toggle-label" htmlFor="kyc-doc-type-active" style={{ width: 'fit-content' }}>
                <input
                  id="kyc-doc-type-active"
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                />
                <div className="toggle-switch"></div>
                <span style={{ marginLeft: 10 }}>Active</span>
              </label>
            </div>
            <button className="btn-primary" type="submit"><FiSave /> Save</button>
          </form>
        </div>
      </div>,
      portalEl
    );

  return (
    <div className="admin-page">
      <div className="page-header flex-header" style={{ position: 'relative', zIndex: 2 }}>
        <div>
          <h1>KYC Document Types</h1>
          <p>Manage document types available for partner KYC submissions.</p>
        </div>
        <button
          ref={addBtnRef}
          type="button"
          className="btn-primary"
          style={{ flexShrink: 0, position: 'relative', zIndex: 3 }}
          aria-haspopup="dialog"
        >
          <FiPlus /> Add Document Type
        </button>
      </div>

      <div className="admin-card table-section">
        {loading ? <div className="skeleton" style={{ height: 220 }} /> : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={rowId(r) || r.name}>
                  <td>#{rowId(r)}</td>
                  <td>{r.name}</td>
                  <td>{r.description || '—'}</td>
                  <td><span className={r.is_active ? 'badge-role partner' : 'badge-role user'}>{r.is_active ? 'Active' : 'Inactive'}</span></td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn-icon" onClick={() => openPanelForEdit(r)}><FiEdit2 /></button>
                      <button type="button" className="btn-icon text-danger" onClick={() => remove(rowId(r))}><FiTrash2 /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalLayer}
    </div>
  );
};

export default KycDocumentTypes;
